import React from 'react';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import GSheetReader from "g-sheets-api";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import FormikForm from 'components/FormikForm';
import CustomButton from 'components/CustomButton';

import { useLoading, useSnackbar } from "contexts";
import { getUsers } from 'services';
import { ROLES } from 'config';
import { saveCodeChallengesInBulk } from 'services/codeChallengeService';
import ReactJson from 'react-json-view';

const SAMPLE_SHEET = "https://docs.google.com/spreadsheets/d/1iX4HSitWXoCSKXg3vVZoo594IjZwPER39Sx81clyZkI/edit?gid=0#gid=0";
export const BootstrapDialogTitle = (props) => {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
};

const container = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    overflow: 'auto',
    minWidth: 650,
};

function ImportChallengeDialog({
    open,
    onSuccess,
    onClose = () => { },
}) {
    const snackbar = useSnackbar();
    const loading = useLoading();
    const [sheetError, setSheetError] = React.useState(false);
    const [apiError, setApiError] = React.useState(null);
    const [clients, setClients] = React.useState([]);

    const handleSave = async ({ sheetUrl, sheetName, client }) => {
        try {
            loading.show();

            setSheetError(false);

            const sheetId = sheetUrl.substring(
                sheetUrl.indexOf("/d/") + 3, sheetUrl.lastIndexOf("/")
            );

            const options = {
                apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
                sheetId,
                sheetName,
                sheetNumber: 1,
                returnAllResults: true,
            };

            GSheetReader(
                options,
                results => {
                    loading.hide();

                    saveCodeChanllenges(results, client);
                },
                error => {
                    loading.hide();

                    setSheetError(true);

                    console.error(error);

                    snackbar.showSnackbar(
                        "Unable to import your data! Something went wrong!", "error", true
                    );
                },
            );
        } catch (error) {
            console.log(error);
            snackbar.showSnackbar(
                "Uh Oh! Unable to import data from sheet", "error", true
            );
            setSheetError(true);
            loading.hide();
        }
    };

    const saveCodeChanllenges = React.useCallback(async (codeChallenges, user) => {
        try {
            loading.show();

            const _codeChallenges = codeChallenges?.map((cc) => ({
                topic: cc.topic,
                subTopic: cc.subTopic,
                title: cc.title,
                difficulty: cc.difficulty,
                score: cc.score,
                time: cc.time,
                companies: cc.companies,
                logicalExplanation: JSON.parse(cc.logicalExplanation || '{}')?.logicalExplanation || '',
                description: cc.description,
                'solutionC++': cc['solutionC++'],
                solutionJava: cc['solutionJava'],
                solutionPython: cc['solutionPython'],
            }));

            await saveCodeChallengesInBulk(_codeChallenges, user);

            snackbar.success("Challenges saved successfully!");
            if (onSuccess && typeof onSuccess === 'function') onSuccess();
            onClose();
        } catch (error) {
            console.error(error);
            setApiError(error);
            snackbar.error(error.message);
        } finally {
            loading.hide();
        }
    }, []);

    React.useEffect(() => {
        if (open) {
            (async () => {
                try {
                    loading.show();

                    const { users } = await getUsers({ roleNames: ROLES.CLIENT });

                    setClients(users);
                } catch (error) {
                    snackbar.showSnackbar(error.message, "error", true);
                } finally {
                    loading.hide();
                }
            })();
        }
    }, [open]);

    return (
        <Dialog onClose={onClose} open={open} maxWidth='lg'>
            <BootstrapDialogTitle id="customized-dialog-title" onClose={onClose}>
                Import Challenges from Google Sheet &nbsp;&nbsp;

                <CustomButton
                    href={SAMPLE_SHEET}
                    target="_blank"
                    rel="noreferrer"
                    referrerPolicy='origin'
                    component="a"
                    variant="outlined"
                    color="primary"
                    width="fit-content"
                >
                    Check Sample Sheet
                </CustomButton>
            </BootstrapDialogTitle>
            <DialogContent sx={container}>
                <Typography variant="h6" color='error' >
                    Setup Google Sheet
                </Typography>
                <Typography variant="body02">
                    <ol >
                        <li>You'll need to make the sheet access available to import questions.</li>
                        <li>In the top right of the screen, hit the <b>Share</b> button.</li>
                        <li>Change the dropdown option to <b>Anyone with the link</b>.</li>
                        <li>Make sure to leave the right hand drop down on <b>Viewer</b>, this way your sheet will remain read-only to the internet.</li>
                        <li>Click <b>Done</b></li>
                    </ol>
                </Typography>

                <Typography variant="h6" color='primary' mb={1}>
                    Add Sheet Details
                </Typography>

                <FormikForm
                    primaryLabel="Import"
                    initialValues={{
                        sheetUrl: "",
                        sheetName: "",
                        client: "",
                    }}
                    fields={[
                        {
                            name: 'sheetUrl',
                            type: 'text',
                            label: 'Google Sheet URL',
                            xs: 12,
                            md: 12,
                        },
                        {
                            name: 'sheetName',
                            type: 'text',
                            label: 'Sheet Name',
                            xs: 12,
                            md: 12,
                        },
                        {
                            name: 'client',
                            type: 'select',
                            label: 'Select client',
                            xs: 12,
                            md: 12,
                        },
                    ]}
                    formOptions={{ "client": clients }}
                    validationSchema={Yup.object().shape({
                        sheetUrl: Yup.string().trim().url().required(),
                        sheetName: Yup.string().trim().required(),
                        client: Yup.string().trim().required(),
                    })}
                    onSubmit={handleSave}
                    onCancel={onClose}
                />

                {sheetError && (
                    <Box mt={1}>
                        <Typography variant="body01" color="error">
                            Unable to import the data from the sheet,
                            It could be possible that:
                        </Typography>
                        <ol >
                            <li>{`You've entered an incorrect Sheet URL or Sheet Name (Default is 'Sheet1').`}</li>
                            <li>{`The Sheet isn't publicly available yet (it's not an instant publish process from Google).`}</li>
                            <li>Some other in-transit error</li>
                        </ol>
                    </Box>
                )}

                {apiError && (
                    <Box mt={2}>
                        <Typography variant="body01" color="error">
                            {apiError.message}
                        </Typography>
                        <ReactJson
                            collapsed={true}
                            enableClipboard={false}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            iconStyle="square"
                            src={JSON.parse(JSON.stringify(apiError.details))}
                            name={'Details'}
                            collapseStringsAfterLength={50}
                            theme="apathy:inverted"

                        />
                    </Box>
                )}

            </DialogContent>
        </Dialog>
    );
}

export default ImportChallengeDialog;